import { exportToSvg } from "@excalidraw/utils/export";
import { useEffect, useState } from "react";

import { COLOR_PALETTE } from "@excalidraw/common";

import { atom, useAtom } from "../editor-jotai";

import type { BinaryFiles, LibraryItem } from "../types";

export type SvgCache = Map<LibraryItem["id"], SVGSVGElement>;

export const libraryItemSvgsCache = atom<SvgCache>(new Map());

const exportLibraryItemToSvg = async (
  elements: LibraryItem["elements"],
  files: BinaryFiles | null,
) => {
  // TODO should pass theme (appState.exportWithDark) - we're still using
  // CSS filter here
  return await exportToSvg({
    elements,
    appState: {
      exportBackground: false,
      viewBackgroundColor: COLOR_PALETTE.white,
    },
    files: files ?? null,
    renderEmbeddables: false,
    skipInliningFonts: true,
  });
};

export const useLibraryItemSvg = (
  id: LibraryItem["id"] | null,
  elements: LibraryItem["elements"] | undefined,
  svgCache: SvgCache,
  ref: React.RefObject<HTMLDivElement | null>,
  files?: BinaryFiles,
): SVGSVGElement | undefined => {
  const [svg, setSvg] = useState<SVGSVGElement>();

  useEffect(() => {
    if (elements) {
      if (id) {
        // Try to load cached svg
        const cachedSvg = svgCache.get(id);

        if (cachedSvg) {
          setSvg(cachedSvg);
        } else {
          // When there is no svg in cache export it and save to cache
          (async () => {
            try {
              const exportedSvg = await exportLibraryItemToSvg(elements, files ?? null);
              // TODO: should likely be removed for custom fonts
              exportedSvg.querySelector(".style-fonts")?.remove();

              if (exportedSvg) {
                svgCache.set(id, exportedSvg);
                setSvg(exportedSvg);
              }
            } catch (err) {
              console.error("Library item SVG export failed:", err);
            }
          })();
        }
      } else {
        // When we have no id (usualy selected items from canvas) just export the svg
        (async () => {
          try {
            const exportedSvg = await exportLibraryItemToSvg(elements, files ?? null);
            setSvg(exportedSvg);
          } catch (err) {
            console.error("Library item SVG export failed:", err);
          }
        })();
      }
    }
  }, [id, elements, svgCache, setSvg, files]);

  useEffect(() => {
    const node = ref.current;

    if (!node) {
      return;
    }

    if (svg) {
      node.innerHTML = svg.outerHTML;
    }

    return () => {
      node.innerHTML = "";
    };
  }, [svg, ref]);

  return svg;
};

export const useLibraryCache = () => {
  const [svgCache] = useAtom(libraryItemSvgsCache);

  const clearLibraryCache = () => svgCache.clear();

  const deleteItemsFromLibraryCache = (items: LibraryItem["id"][]) => {
    items.forEach((item) => svgCache.delete(item));
  };

  return {
    clearLibraryCache,
    deleteItemsFromLibraryCache,
    svgCache,
  };
};
